import { TreeNode } from 'primeng/api';

/**
 * Builds the Practice -> Career Path -> Category tree used by the pool and
 * evaluation filter pickers.
 *
 * Both screens previously built this list themselves, and both stopped at two
 * levels: career paths at the top with categories underneath. Practice was only
 * carried as a property on the leaves, so unrelated career paths from different
 * practices (Management, Banking, Telecom, ...) sat side by side at the same
 * level with nothing showing which practice they belonged to.
 *
 * Leaf nodes keep the exact shape the two forms already persist - `key`, `label`,
 * `shortLabel`, `practice`, `careerPath` - so previously saved selections still
 * match by key and nothing needs migrating.
 */

/**
 * Turns saved selection keys back into the actual nodes of `tree`.
 *
 * Both forms used to rebuild their saved selection as fresh plain objects.
 * PrimeNG decides whether a node is selected by object identity, so those
 * stand-ins matched nothing in the tree: restored ticks did not count towards
 * their parent, and propagateUp() left career paths and practices unchecked even
 * when every category beneath them was ticked.
 *
 * Parents are marked here as well, mirroring PrimeNG's own propagateUp rules -
 * a fully selected parent joins the selection, a partly selected one gets
 * `partialSelected` - because PrimeNG only applies that logic to user clicks,
 * never to a selection that is assigned programmatically.
 */
export function resolveSelection(tree: TreeNode[], savedKeys: Iterable<string>): TreeNode[] {
  const keys = new Set(savedKeys);
  const selected: TreeNode[] = [];

  // Count how many leaves answer to each legacy key. A legacy key left the
  // practice out, so it can point at more than one category when two practices
  // hold career paths with the same name. Those are only honoured when they
  // identify exactly one leaf - otherwise restoring a selection in one practice
  // would tick its namesake in the other.
  const leavesByLegacyKey = new Map<string, number>();
  const countLegacyKeys = (node: TreeNode): void => {
    const children = node.children ?? [];
    if (children.length === 0) {
      const legacyKey = (node as TreeNode & { legacyKey?: string }).legacyKey;
      if (legacyKey) {
        leavesByLegacyKey.set(legacyKey, (leavesByLegacyKey.get(legacyKey) ?? 0) + 1);
      }
      return;
    }
    children.forEach(countLegacyKeys);
  };
  tree.forEach(countLegacyKeys);

  /** Returns true when this node and everything beneath it is selected. */
  const visit = (node: TreeNode): boolean => {
    const children = node.children ?? [];

    if (children.length === 0) {
      const legacyKey = (node as TreeNode & { legacyKey?: string }).legacyKey;
      const isSelected =
        keys.has(String(node.key)) ||
        (legacyKey !== undefined &&
          keys.has(legacyKey) &&
          leavesByLegacyKey.get(legacyKey) === 1);

      if (isSelected) {
        selected.push(node);
      }
      return isSelected;
    }

    let fullySelectedChildren = 0;
    let anyPartial = false;
    for (const child of children) {
      if (visit(child)) {
        fullySelectedChildren++;
      } else if (child.partialSelected) {
        anyPartial = true;
      }
    }

    if (fullySelectedChildren === children.length) {
      node.partialSelected = false;
      selected.push(node);
      return true;
    }

    node.partialSelected = fullySelectedChildren > 0 || anyPartial;
    return false;
  };

  tree.forEach(visit);
  return selected;
}

/**
 * Minimal structural shapes, rather than importing CareerPath / CategoryMapping.
 * Only these fields are used, so the builder stays testable with small fixtures
 * and callers can pass their real models without a cast.
 */
export interface CareerPathLike {
  name: string;
  practice?: string | null;
  id?: number | null;
}

export interface CategoryLike {
  categoryLongLabel: string;
  careerPath?: { id?: number | null; name: string; practice?: string | null } | null;
  categoryMappingsId: {
    careerPathId?: number | null;
    division: string;
    categoryShortLabel: string;
  };
}

/** Shown when a career path has no practice recorded, so its entries stay reachable. */
export const NO_PRACTICE_LABEL = 'Unassigned';

/**
 * Separates the parts of a composite key.
 *
 * A NUL cannot occur in a practice, a name, a label or a division, so no two
 * different sets of parts can produce the same string. Concatenating them
 * directly could: "AB" + "C" and "A" + "BC" are indistinguishable afterwards.
 */
const SEPARATOR = '\u0000';

/** The practice a career path is filed under, with a home for those missing one. */
function practiceOf(practice?: string | null): string {
  return practice?.trim() ? practice : NO_PRACTICE_LABEL;
}

/** The career path a category belongs to, from whichever field carries it. */
function careerPathIdOf(category: CategoryLike): number | null {
  return category.careerPath?.id ?? category.categoryMappingsId.careerPathId ?? null;
}

/**
 * Stable identity for a category leaf.
 *
 * Kept byte-for-byte compatible with the previous implementation (base64 of
 * careerPath + longLabel + division) so selections saved before this change still
 * resolve. btoa throws on non-Latin1 characters, hence the fallback.
 */
function encode(raw: string): string {
  // btoa throws on characters outside Latin1, hence the fallback.
  try {
    return btoa(raw);
  } catch {
    return raw;
  }
}

/**
 * The key format used before practices became a level.
 *
 * It leaves the practice out, so two practices holding career paths with the
 * same name (a "General" under both BPS and BSA, say) produced byte-identical
 * keys for their categories. Selecting one then restored both, because the
 * saved selection is matched by key.
 *
 * Still computed so selections saved under the old format keep resolving - see
 * resolveSelection, which accepts either key.
 */
function legacyCategoryKey(category: CategoryLike): string {
  return encode(
    [
      category.careerPath?.name ?? '',
      category.categoryLongLabel,
      category.categoryMappingsId.division,
    ].join('')
  );
}

/** Includes the practice, so categories cannot collide across practices. */
function categoryKey(category: CategoryLike): string {
  return encode(
    [
      category.careerPath?.practice ?? '',
      category.careerPath?.name ?? '',
      category.categoryLongLabel,
      category.categoryMappingsId.division,
    ].join(SEPARATOR)
  );
}

function categoryNode(category: CategoryLike): TreeNode {
  return {
    key: categoryKey(category),
    legacyKey: legacyCategoryKey(category),
    label: `${category.categoryLongLabel} | ${category.categoryMappingsId.division}`,
    practice: category.careerPath?.practice ?? null,
    careerPath: category.careerPath?.name ?? '',
    shortLabel: category.categoryMappingsId.categoryShortLabel,
    division: category.categoryMappingsId.division,
    leaf: true,
  } as TreeNode;
}

/**
 * Groups career paths under their practice and their categories underneath.
 *
 * Career paths with no categories are dropped: they would render as an empty
 * branch that cannot be expanded or usefully ticked. A practice left with no
 * career paths is dropped for the same reason.
 */
export function buildCategoryTree(
  careerPaths: CareerPathLike[],
  categories: CategoryLike[]
): TreeNode[] {
  // Categories are matched to their career path by id where both sides carry one.
  // Matching on the name alone is not safe: names repeat across practices (a
  // "General" under both BPS and BSA) and even within one (MANAGEMENT and
  // Management are separate rows). Every career path then received every
  // same-named practice's categories, so a category showed up under a practice it
  // does not belong to and ticking it there ticked its twin. Ids are unique, so
  // this cannot happen. The name is the fallback for callers that omit ids.
  const matchById =
    careerPaths.every((careerPath) => careerPath.id != null) &&
    categories.every((category) => careerPathIdOf(category) != null);

  const bucketKey = (
    id: number | null | undefined,
    practice: string | null | undefined,
    name: string
  ): string => (matchById ? `id:${id}` : `name:${practiceOf(practice)}${SEPARATOR}${name}`);

  const categoriesByCareerPath = new Map<string, CategoryLike[]>();
  for (const category of categories) {
    const name = category.careerPath?.name;
    if (!name) {
      continue;
    }
    const key = bucketKey(careerPathIdOf(category), category.careerPath?.practice, name);
    const bucket = categoriesByCareerPath.get(key);
    if (bucket) {
      bucket.push(category);
    } else {
      categoriesByCareerPath.set(key, [category]);
    }
  }

  // Preserve the order the API returned rather than re-sorting, so the list stays
  // consistent with the other screens that show these values.
  const practices = new Map<string, TreeNode[]>();

  for (const careerPath of careerPaths) {
    const key = bucketKey(careerPath.id, careerPath.practice, careerPath.name);
    const children = (categoriesByCareerPath.get(key) ?? []).map(categoryNode);
    if (children.length === 0) {
      continue;
    }

    const practice = practiceOf(careerPath.practice);
    const careerPathNode: TreeNode = {
      // The id is what makes this unique - two career paths in one practice can
      // share a name. The name is kept as well because it is the readable half.
      key: `practice:${practice}|careerPath:${careerPath.id ?? careerPath.name}`,
      label: careerPath.name,
      selectable: true,
      children,
    };

    const existing = practices.get(practice);
    if (existing) {
      existing.push(careerPathNode);
    } else {
      practices.set(practice, [careerPathNode]);
    }
  }

  return Array.from(practices.entries()).map(([practice, careerPathNodes]) => {
    const practiceNode: TreeNode = {
      key: `practice:${practice}`,
      label: practice,
      selectable: true,
      children: careerPathNodes,
    };

    // PrimeNG only propagates a checkbox up the tree when the node carries a
    // `parent` reference - see propagateUp(), which is guarded by
    // `this.propagateSelectionUp && node.parent`. Without these links, ticking
    // every category under a career path left both the career path and the
    // practice unchecked.
    for (const careerPathNode of careerPathNodes) {
      careerPathNode.parent = practiceNode;
      for (const category of careerPathNode.children ?? []) {
        category.parent = careerPathNode;
      }
    }

    return practiceNode;
  });
}

/**
 * Reads the practice out of a saved category key.
 *
 * The key is the base64 of practice \0 careerPath \0 category \0 division, so the
 * practice can be recovered from the key alone. Selections saved before the
 * practice became part of the key use the legacy format and have none - they are
 * reported as null.
 */
export function practiceOfCategoryKey(key: string | null | undefined): string | null {
  if (!key) {
    return null;
  }
  let decoded: string;
  try {
    decoded = atob(key);
  } catch {
    return null;
  }
  const parts = decoded.split(SEPARATOR);
  // The legacy key has three parts (careerPath, category, division) and no practice.
  if (parts.length < 4) {
    return null;
  }
  return parts[0]?.trim() || null;
}
